import KybCorporateAccessRepository from "@/core/kyb/repositories/KybCorporateAccessRepository";
import {
  KybCorporateAccess,
  KybCorporateAccessCreated,
  KybCorporateAccessProfile,
  KybCorporateAccessRole,
  KybCorporateAccessStatus,
  KybCorporateMfaChallenge,
  KybCorporateUser,
  KybCorporateUserStatus,
  KybCorporateUserWithAccesses,
  ReactivateCorporateUserInput,
  SaveAccessProfileInput,
} from "@/core/kyb/entities/KybCorporateAccess";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import { RepositoryResult, RepositoryResultFactory } from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { executeKybRequest, toKybException } from "../../http/services/kybRequest/kybRequest";
import kybCorporateAccessEndpoints from "../../api/kybCorporateAccessEndpoints/kybCorporateAccessEndpoints";

type UserDto = { corporateUserId: string; personId: string; status: KybCorporateUserStatus };
type AccessDto = {
  accessId: string;
  corporateUserId: string;
  mandateId: string;
  status: KybCorporateAccessStatus;
  createdBy: string;
  createdAt: string;
};
type CreatedDto = { user: UserDto; access: AccessDto; userCreated: boolean };
type ProfileDto = {
  accessId: string;
  roles: KybCorporateAccessRole[];
  accounts: string[];
  operations: string[];
  operationLimits: Record<string, number>;
  countries: string[];
  currencies: string[];
  assignedBy: string;
  updatedAt: string;
};
type ChallengeDto = { sentTo: string; ttlMinutes: number; testCode?: string };

const mapUser = (dto: UserDto): KybCorporateUser => ({
  corporateUserId: dto.corporateUserId,
  personId: dto.personId,
  status: dto.status,
});

const mapAccess = (dto: AccessDto): KybCorporateAccess => ({
  accessId: dto.accessId,
  corporateUserId: dto.corporateUserId,
  mandateId: dto.mandateId,
  status: dto.status,
  createdBy: dto.createdBy,
  createdAt: dto.createdAt,
});

const mapCreated = (dto: CreatedDto): KybCorporateAccessCreated => ({
  user: mapUser(dto.user),
  access: mapAccess(dto.access),
  userCreated: dto.userCreated,
});

const mapProfile = (dto: ProfileDto): KybCorporateAccessProfile => ({
  accessId: dto.accessId,
  roles: dto.roles,
  accounts: dto.accounts,
  operations: dto.operations,
  operationLimits: { ...dto.operationLimits },
  countries: dto.countries,
  currencies: dto.currencies,
  assignedBy: dto.assignedBy,
  updatedAt: dto.updatedAt,
});

const mapChallenge = (dto: ChallengeDto): KybCorporateMfaChallenge => ({
  sentTo: dto.sentTo,
  ttlMinutes: dto.ttlMinutes,
  testCode: dto.testCode,
});

export default class KybCorporateAccessRepositoryBackend implements KybCorporateAccessRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async listCorporateAccess(caseId: string): Promise<RepositoryResult<KybCorporateUserWithAccesses[]>> {
    return executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<{ user: UserDto; accesses: AccessDto[] }[]>>({
          url: kybCorporateAccessEndpoints.list(caseId),
          withAuth: true,
        }),
      (dtos) => dtos.map((dto) => ({ user: mapUser(dto.user), accesses: dto.accesses.map(mapAccess) })),
    );
  }

  async createCorporateAccess(caseId: string, mandateId: string): Promise<RepositoryResult<KybCorporateAccessCreated>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<CreatedDto>, undefined>({
          url: kybCorporateAccessEndpoints.create(caseId, mandateId),
          withAuth: true,
        }),
      mapCreated,
    );
  }

  async requestReactivationMfa(caseId: string, mandateId: string): Promise<RepositoryResult<KybCorporateMfaChallenge>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<ChallengeDto>, undefined>({
          url: kybCorporateAccessEndpoints.reactivationMfaChallenge(caseId, mandateId),
          withAuth: true,
        }),
      mapChallenge,
    );
  }

  async reactivateCorporateUser(
    caseId: string,
    mandateId: string,
    input: ReactivateCorporateUserInput,
  ): Promise<RepositoryResult<KybCorporateAccessCreated>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<CreatedDto>, ReactivateCorporateUserInput>({
          url: kybCorporateAccessEndpoints.reactivation(caseId, mandateId),
          body: input,
          withAuth: true,
        }),
      mapCreated,
    );
  }

  async getAccessProfile(
    caseId: string,
    accessId: string,
  ): Promise<RepositoryResult<KybCorporateAccessProfile | null>> {
    const result = await executeKybRequest(
      () =>
        this.httpClient.get<ApiResponse<ProfileDto>>({
          url: kybCorporateAccessEndpoints.profile(caseId, accessId),
          withAuth: true,
        }),
      (dto): KybCorporateAccessProfile | null => mapProfile(dto),
    );
    // Aucun profil affecté : le back répond 404, ce n'est pas une erreur pour l'écran.
    if (!result.success && result.exception?.type === DefaultExceptionTypes.NOT_FOUND) {
      return RepositoryResultFactory.success(null);
    }
    return result;
  }

  async requestProfileMfa(caseId: string, accessId: string): Promise<RepositoryResult<KybCorporateMfaChallenge>> {
    return executeKybRequest(
      () =>
        this.httpClient.post<ApiResponse<ChallengeDto>, undefined>({
          url: kybCorporateAccessEndpoints.profileMfaChallenge(caseId, accessId),
          withAuth: true,
        }),
      mapChallenge,
    );
  }

  async saveAccessProfile(
    caseId: string,
    accessId: string,
    input: SaveAccessProfileInput,
  ): Promise<RepositoryResult<KybCorporateAccessProfile>> {
    try {
      const response = await this.httpClient.put<ApiResponse<ProfileDto>, SaveAccessProfileInput>({
        url: kybCorporateAccessEndpoints.profile(caseId, accessId),
        body: input,
        withAuth: true,
      });
      return RepositoryResultFactory.success(mapProfile(response.data));
    } catch (error) {
      return RepositoryResultFactory.error(toKybException(error));
    }
  }
}
